/**
 * DepartmentMemberWorkload
 * Displays member workload with utilization heatmap
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Users,
  Search,
  AlertTriangle,
  CheckCircle,
  Activity,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

import { DepartmentMemberWorkload as DeptMemberWorkload, UserRole } from '@/types';

interface DepartmentMemberWorkloadProps {
  workload: DeptMemberWorkload | undefined;
  isLoading: boolean;
}

type SortField = 'userName' | 'utilizationPercent' | 'activeTasks' | 'performanceRating';
type SortDirection = 'asc' | 'desc';

const DepartmentMemberWorkload: React.FC<DepartmentMemberWorkloadProps> = ({
  workload,
  isLoading,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('utilizationPercent');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} />
          <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!workload) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No workload data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const getUtilizationColor = (util: number) => {
    if (util > 100) return theme.palette.error.main;
    if (util >= 80) return theme.palette.warning.main;
    if (util >= 60) return theme.palette.success.main;
    return theme.palette.info.main;
  };

  const getUtilizationLabel = (util: number) => {
    if (util > 100) return 'Overloaded';
    if (util >= 80) return 'Busy';
    if (util >= 60) return 'Optimal';
    return 'Available';
  };

  const getRoleLabel = (role: UserRole) => {
    const labels: Record<UserRole, string> = {
      [UserRole.SUPER_ADMIN]: 'Super Admin',
      [UserRole.ADMIN]: 'Admin',
      [UserRole.DEPARTMENT_HEAD]: 'Dept Head',
      [UserRole.PROJECT_MANAGER]: 'PM',
      [UserRole.TEAM_LEAD]: 'Team Lead',
      [UserRole.INDIVIDUAL_CONTRIBUTOR]: 'IC',
      [UserRole.MANAGER]: 'Manager',
      [UserRole.ENGINEER]: 'Engineer',
    };
    return labels[role] || role;
  };

  // Filter and sort members
  const filteredMembers = workload.members
    .filter(member => 
      member.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.teamName.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * direction;
      }
      return ((aValue as number) - (bValue as number)) * direction;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Member Workload - {workload.departmentName}
            </Typography>
          </Box>
          
          <TextField
            size="small"
            placeholder="Search members or teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Users size={20} style={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {workload.summary.totalMembers}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Members</Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'error.50',
              border: '1px solid',
              borderColor: 'error.200',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <AlertTriangle size={20} style={{ color: theme.palette.error.main }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="error.main">
                {workload.summary.overloaded}
              </Typography>
              <Typography variant="caption" color="text.secondary">Overloaded</Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'success.50',
              border: '1px solid',
              borderColor: 'success.200',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CheckCircle size={20} style={{ color: theme.palette.success.main }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {workload.summary.optimal}
              </Typography>
              <Typography variant="caption" color="text.secondary">Optimal</Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'info.50',
              border: '1px solid',
              borderColor: 'info.200',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Activity size={20} style={{ color: theme.palette.info.main }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {workload.summary.available}
              </Typography>
              <Typography variant="caption" color="text.secondary">Available</Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.300',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ml: 'auto',
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {workload.summary.avgUtilization}%
              </Typography>
              <Typography variant="caption" color="text.secondary">Avg Utilization</Typography>
            </Box>
          </Box>
        </Box>

        {/* Members Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'userName'}
                    direction={sortField === 'userName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('userName')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'utilizationPercent'}
                    direction={sortField === 'utilizationPercent' ? sortDirection : 'asc'}
                    onClick={() => handleSort('utilizationPercent')}
                  >
                    Utilization
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Hours</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'activeTasks'}
                    direction={sortField === 'activeTasks' ? sortDirection : 'asc'}
                    onClick={() => handleSort('activeTasks')}
                  >
                    Active Tasks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'performanceRating'}
                    direction={sortField === 'performanceRating' ? sortDirection : 'asc'}
                    onClick={() => handleSort('performanceRating')}
                  >
                    Performance
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow 
                  key={member.userId}
                  sx={{ 
                    '&:hover': { bgcolor: 'grey.50' },
                    bgcolor: member.isOverloaded ? alpha(theme.palette.error.main, 0.05) : 'transparent',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                        {member.userName.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {member.userName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {member.teamName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getRoleLabel(member.role)} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        sx={{ color: getUtilizationColor(member.utilizationPercent) }}
                      >
                        {member.utilizationPercent}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(member.utilizationPercent, 100)}
                        sx={{
                          width: 60,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getUtilizationColor(member.utilizationPercent),
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {member.loggedHours}/{member.allocatedHours}h
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={500}>
                      {member.activeTasks}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {member.performanceRating >= 4 ? (
                        <TrendingUp size={14} style={{ color: theme.palette.success.main }} />
                      ) : member.performanceRating >= 3.5 ? (
                        <Activity size={14} style={{ color: theme.palette.warning.main }} />
                      ) : (
                        <TrendingDown size={14} style={{ color: theme.palette.error.main }} />
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {member.performanceRating.toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getUtilizationLabel(member.utilizationPercent)}
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: alpha(getUtilizationColor(member.utilizationPercent), 0.1),
                        color: getUtilizationColor(member.utilizationPercent),
                        border: '1px solid',
                        borderColor: alpha(getUtilizationColor(member.utilizationPercent), 0.3),
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredMembers.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No members found matching your search
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DepartmentMemberWorkload;
